import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Music, Info } from "lucide-react";
import { toast } from "sonner";
import { Device, deviceService } from "@/services/deviceService";

interface SingleDeviceMusicScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  device: Device | null;
  onSuccess?: () => void;
}

const DAYS = [
  { id: "monday", label: "Pazartesi" },
  { id: "tuesday", label: "Salı" },
  { id: "wednesday", label: "Çarşamba" },
  { id: "thursday", label: "Perşembe" },
  { id: "friday", label: "Cuma" },
  { id: "saturday", label: "Cumartesi" },
  { id: "sunday", label: "Pazar" },
];

export default function SingleDeviceMusicScheduleDialog({
  open,
  onOpenChange,
  device,
  onSuccess,
}: SingleDeviceMusicScheduleDialogProps) {
  const [startTime, setStartTime] = useState("");
  const [stopTime, setStopTime] = useState("");
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const handleDayToggle = (dayId: string) => {
    setSelectedDays(prev =>
      prev.includes(dayId)
        ? prev.filter(d => d !== dayId)
        : [...prev, dayId]
    );
  };

  const handleSelectAllDays = () => {
    if (selectedDays.length === DAYS.length) {
      setSelectedDays([]);
    } else {
      setSelectedDays(DAYS.map(d => d.id));
    }
  };

  const handleSubmit = async () => {
    if (!device) return;

    if (!startTime || !stopTime) {
      toast.error("Lütfen başlangıç ve bitiş saati seçin");
      return;
    }

    if (selectedDays.length === 0) {
      toast.error("Lütfen en az bir gün seçin");
      return;
    }

    if (startTime >= stopTime) {
      toast.error("Bitiş saati başlangıç saatinden sonra olmalıdır");
      return;
    }

    try {
      setLoading(true);

      await deviceService.scheduleMusicPlayback(device._id, {
        startDateTime: startTime,
        endDateTime: stopTime,
        days: selectedDays,
      });

      toast.success(
        `${device.name} için müzik zamanlaması oluşturuldu`
      );

      onOpenChange(false);
      resetForm();
      onSuccess?.();
    } catch (error) {
      toast.error("Müzik zamanlama sırasında hata oluştu");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStartTime("");
    setStopTime("");
    setSelectedDays([]);
  };

  const isFormValid = () => {
    return startTime && stopTime && selectedDays.length > 0 && startTime < stopTime;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Music className="w-5 h-5" />
            Planlı Çalma - {device?.name}
          </DialogTitle>
        </DialogHeader>

        {/* Bilgilendirme Banner */}
        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p>
              Seçilen günlerde müzik belirtilen saatlerde otomatik olarak başlayacak ve duracaktır.
            </p>
          </div>
        </div>

        {/* Zaman Seçimi */}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start-time" className="text-sm font-medium">
                Başlangıç Saati
              </Label>
              <Input
                id="start-time"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="stop-time" className="text-sm font-medium">
                Bitiş Saati
              </Label>
              <Input
                id="stop-time"
                type="time"
                value={stopTime}
                onChange={(e) => setStopTime(e.target.value)}
                className="w-full"
              />
            </div>
          </div>

          {/* Gün Seçimi */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Günler</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleSelectAllDays}
                className="h-8 text-xs"
              >
                {selectedDays.length === DAYS.length ? "Tümünü Kaldır" : "Tümünü Seç"}
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {DAYS.map((day) => (
                <div key={day.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`single-${day.id}`}
                    checked={selectedDays.includes(day.id)}
                    onChange={() => handleDayToggle(day.id)}
                  />
                  <label
                    htmlFor={`single-${day.id}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {day.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Özet */}
          {startTime && stopTime && selectedDays.length > 0 && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Özet:</span> Müzik her{" "}
                {selectedDays.length === 7
                  ? "gün"
                  : selectedDays.map(d => DAYS.find(day => day.id === d)?.label).join(", ")}{" "}
                saat <span className="font-medium">{startTime}</span>'de başlayacak ve{" "}
                <span className="font-medium">{stopTime}</span>'de duracaktır.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              resetForm();
            }}
            disabled={loading}
          >
            İptal
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid() || loading}
          >
            {loading ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
